import {
  Children, Fragment, isValidElement, useContext, useEffect, useMemo, useReducer, useRef, useState,
  type CSSProperties, type ReactNode,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { QueryClientContext, type Query } from '@tanstack/react-query';
import { Check, ChevronDown, X, Search as SearchIcon } from 'lucide-react';
import { cn } from './cn';
import { Spinner } from './Button';
import { useFormContext } from './Form';
import { useWheelScroll } from './useWheelScroll';
import type { ControlSize } from './Input';

type Val = string | number;

export interface SelectOption {
  value: Val;
  label?: ReactNode;
  disabled?: boolean;
  title?: string;
  /** Plain text an option is searched by when its label is a node (optionFilterProp="searchLabel"). */
  searchLabel?: string;
  /** What the collapsed trigger shows instead of the label (optionLabelProp="selectedText"). */
  selectedText?: ReactNode;
  /**
   * The colour this option's chip wears once chosen (`mode="multiple"`) — a status tone, a
   * warehouse's identity hue. A selected value that is a coloured tag in the list and a grey chip in
   * the trigger is two different things wearing one name; with a hue the control shows the thing you
   * are about to be looking at. Omit for the neutral chip.
   */
  chipHue?: string;
  /**
   * How many rows picking this option leaves — drawn as a badge by `renderFacetOption`
   * (lib/facetOptions), never glued into the label. Declared here because an undeclared `count` on
   * an inline options literal crashed tsc outright ("Debug Failure. No error for last overload
   * signature") on LowStockAlertsPage instead of reporting an error.
   */
  count?: number | null;
  children?: ReactNode; // when authored as <Select.Option>text</Select.Option>
}

export interface SelectProps {
  value?: Val | Val[] | null;
  defaultValue?: Val | Val[];
  onChange?: (value: any, option?: any) => void;
  options?: (SelectOption | { label?: ReactNode; options: SelectOption[] })[];
  children?: ReactNode;
  placeholder?: ReactNode;
  disabled?: boolean;
  size?: ControlSize | 'small' | 'middle' | 'large';
  invalid?: boolean;
  status?: 'error' | 'warning';
  className?: string;
  style?: CSSProperties;
  allowClear?: boolean;
  showSearch?: boolean;
  mode?: 'multiple' | 'tags';
  loading?: boolean;
  optionFilterProp?: string;
  /** antd: which option field to render as the selected value (e.g. "text"). Defaults to label. */
  optionLabelProp?: string;
  /** antd: custom render for each dropdown option (receives the raw option). */
  optionRender?: (option: any, info?: { index: number }) => ReactNode;
  filterOption?: boolean | ((input: string, option: SelectOption) => boolean);
  notFoundContent?: ReactNode;
  maxTagCount?: number | 'responsive';
  onSearch?: (v: string) => void;
  onClear?: () => void;
  popupMatchSelectWidth?: boolean;
  dropdownStyle?: CSSProperties;
  autoFocus?: boolean;
  id?: string;
  'aria-label'?: string;
  /** tags mode: characters that commit a token (Enter always commits). */
  tokenSeparators?: string[];
  /** antd: force the dropdown open/closed. Only `false` (never open) is honored. */
  open?: boolean;
  /** antd: custom suffix; `null` hides the chevron. */
  suffixIcon?: ReactNode | null;
}

const SIZE: Record<ControlSize, string> = { sm: 'min-h-8 text-sm', md: 'min-h-9 text-sm', lg: 'min-h-11 text-[15px]' };
const normSize = (s?: SelectProps['size']): ControlSize => (s === 'small' ? 'sm' : s === 'middle' ? 'md' : s === 'large' ? 'lg' : (s as ControlSize) ?? 'md');

/**
 * The searchable text of an option.
 *
 * A rendered label may be JSX — an icon beside a name, a name over a location capsule — and there is
 * no text to search in a React element. Falling straight to the value meant typing a dealer's town
 * into such a list searched the row id and matched nothing; `title` is the plain-text form the option
 * already carries for exactly this purpose, so it is what a search should read.
 */
const labelText = (o: SelectOption): string => {
  const l = o.label ?? o.children;
  if (typeof l === 'string' || typeof l === 'number') return String(l);
  // A node label has no text to search. Fall back to the plain text the option carries for exactly that (`searchLabel`),
  // then its title — without it, typing "HDFC" into a bank-account picker searched the account's id and found nothing.
  return o.searchLabel ?? o.title ?? String(o.value);
};

// Extract <Select.Option>/<Select.OptGroup> children into an options array.
function fromChildren(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = [];
  Children.forEach(children, (c) => {
    if (!isValidElement(c)) return;
    const p: any = c.props;
    if (p && p.value !== undefined) out.push({ value: p.value, label: p.label ?? p.children, disabled: p.disabled });
    else if (p && p.children) out.push(...fromChildren(p.children)); // OptGroup
  });
  return out;
}

/** A failure worth retrying: no answer at all (network, API restarting — api/client marks those
 *  `status: 0`) or the server fell over. A 4xx IS an answer — refused, not found — and asking again
 *  changes nothing. */
const isTransientError = (e: any) => {
  const s = e?.response?.status;
  return !s || s >= 500 || s === 408 || s === 429;
};

/**
 * WHY IS THIS DROPDOWN EMPTY?
 *
 * Nearly every option list in the app is `data ?? []` off a query, so a list that is still loading or
 * whose request FAILED renders exactly like a list with nothing in it — and the dropdown said "No
 * results", i.e. stated as fact that there are no warehouses / products / people. Caught on the
 * Transfers filter (2026-09-14): both warehouse boxes said "No results" for the rest of the session
 * because `/warehouses/my` had failed once while the API restarted, and nothing ever asked again.
 *
 * A Select cannot see which query fed its options, but it can see the page's queries. So when it is
 * opened EMPTY: a first load still in flight → "Loading…"; a mounted query that failed with no data
 * → retry it once, straight away (the usual cause is transient, so the list just appears), and only if
 * that fails too say "didn't load" with a Try again. Nothing wrong anywhere → the normal empty text.
 * Works outside a QueryClientProvider (the confirm dialog's own root) by simply doing nothing.
 */
function useEmptyListCause(active: boolean) {
  const qc = useContext(QueryClientContext);
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const autoRetried = useRef(false);

  useEffect(() => {
    if (!active || !qc) { autoRetried.current = false; return; }
    return qc.getQueryCache().subscribe(() => rerender());
  }, [active, qc]);

  let loading = false;
  let failed: Query[] = [];
  if (active && qc) {
    const noData = (q: Query) => q.state.data === undefined;
    const mounted = qc.getQueryCache().findAll({ type: 'active' });
    loading = mounted.some((q) => noData(q) && q.state.fetchStatus === 'fetching');
    failed = mounted.filter((q) => noData(q) && q.state.status === 'error' && q.state.fetchStatus === 'idle' && isTransientError(q.state.error));
  }

  const retry = () => {
    if (!qc || !failed.length) return;
    const set = new Set(failed);
    void qc.refetchQueries({ predicate: (q) => set.has(q) });
  };

  useEffect(() => {
    if (active && failed.length && !autoRetried.current) { autoRetried.current = true; retry(); }
  });

  return { cause: loading ? 'loading' as const : failed.length ? 'failed' as const : null, retry };
}

function OptionComp(_props: { value: Val; label?: ReactNode; disabled?: boolean; children?: ReactNode }) { return null; }
function OptGroup(_props: { label?: ReactNode; children?: ReactNode }) { return null; }

/**
 * antd `Select mode="tags"` — free-form token input: type + Enter (or a tokenSeparator)
 * to add a value not in any options list, chips are removable. No dropdown when `open={false}`.
 */
function SelectTags({ value, onChange, placeholder, disabled, size, className, style, allowClear, suffixIcon, tokenSeparators, invalid, status, id, ...aria }: SelectProps) {
  const [text, setText] = useState('');
  const vals: Val[] = Array.isArray(value) ? value : value == null ? [] : [value];
  const sz = normSize(size);
  const isErr = invalid || status === 'error';
  const commit = (raw: string) => {
    const t = raw.trim();
    if (t && !vals.some((v) => String(v) === t)) onChange?.([...vals, t], undefined);
    setText('');
  };
  const removeAt = (i: number) => onChange?.(vals.filter((_, idx) => idx !== i), undefined);
  const seps = tokenSeparators ?? [];
  return (
    <div
      style={style}
      onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}
      className={cn(
        'flex w-full flex-wrap items-center gap-1 rounded-control border bg-surface px-2 py-1 text-content transition-colors focus-within:ring-2 focus-within:ring-brand-500/30',
        isErr ? 'border-danger-500' : 'border-line focus-within:border-brand-400',
        disabled && 'cursor-not-allowed opacity-60',
        SIZE[sz], className,
      )}
    >
      {vals.map((v, i) => (
        <span key={`${String(v)}-${i}`} className="inline-flex items-center gap-1 rounded bg-surface-3 px-1.5 py-0.5 text-xs text-content">
          {String(v)}
          {!disabled && (
            <span role="button" tabIndex={-1} aria-label="Remove" onClick={(e) => { e.stopPropagation(); removeAt(i); }} className="text-subtle hover:text-content">
              <X className="size-3" />
            </span>
          )}
        </span>
      ))}
      <input
        id={id}
        aria-label={aria['aria-label'] ?? (typeof placeholder === 'string' ? placeholder : undefined)}
        value={text}
        disabled={disabled}
        placeholder={vals.length ? '' : (typeof placeholder === 'string' ? placeholder : '')}
        onChange={(e) => {
          const val = e.target.value;
          if (seps.length && seps.some((s) => val.includes(s))) {
            const parts = val.split(new RegExp(seps.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')));
            parts.slice(0, -1).forEach(commit);
            setText(parts[parts.length - 1] ?? '');
          } else setText(val);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(text); }
          else if (e.key === 'Backspace' && text === '' && vals.length) removeAt(vals.length - 1);
        }}
        className="min-w-[60px] flex-1 bg-transparent text-sm outline-none placeholder:text-subtle"
      />
      {suffixIcon !== null && allowClear && vals.length > 0 && !disabled && (
        <span role="button" tabIndex={-1} aria-label="Clear" onClick={(e) => { e.stopPropagation(); onChange?.([], undefined); }} className="text-subtle hover:text-content">
          <X className="size-4" />
        </span>
      )}
    </div>
  );
}

/**
 * Ant Design `Select` replacement — single OR multiple, optional search, clearable.
 * Backward-compatible with the earlier single-select ui API (value:string, onChange:(v)=>void).
 * Built on Radix Popover (portalled, positioned, focus-safe).
 */
function SelectBase(props: SelectProps) {
  // tags mode is a free-form token input — a fully separate, self-contained render path.
  if (props.mode === 'tags') return <SelectTags {...props} />;
  return <SelectSingleOrMultiple {...props} />;
}

function SelectSingleOrMultiple({
  value, onChange, options, children, placeholder = 'Select', disabled, size, invalid, status,
  className, style, allowClear, showSearch, mode, loading, optionFilterProp, optionLabelProp, optionRender, filterOption,
  notFoundContent = 'No results', maxTagCount, onSearch, onClear, id, tokenSeparators, open: _openProp, suffixIcon, ...aria
}: SelectProps) {
  void tokenSeparators; void _openProp; void suffixIcon;
  // antd parity: a Select fills its Form.Item inside a form, but is content-sized (auto width,
  // honoring an explicit style.width/minWidth) when standalone — e.g. in a wrap-flex filter bar.
  // Callers can always force a width via `style` or a `w-…` className.
  const inForm = useFormContext() != null;
  const widthCls = inForm ? 'w-full' : 'w-auto min-w-[7rem] max-w-full';
  // The label shown for a SELECTED value: `optionLabelProp` (antd) wins, else label/children/value.
  const chipLabel = (o: SelectOption): ReactNode =>
    (optionLabelProp ? (o as any)[optionLabelProp] : undefined) ?? o.label ?? o.children ?? String(o.value);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Highlighted (keyboard-active) option index. Starts at -1 = NOTHING pre-highlighted, so opening a
  // dropdown never makes the first option look selected/active before the user hovers or arrows to it.
  // (Typing a search sets it to 0 so Enter picks the top match — see the search onChange.)
  const [hi, setHi] = useState(-1);
  const searchRef = useRef<HTMLInputElement>(null);
  // Read by the close effect below without re-running it on every keystroke.
  const queryRef = useRef(query);
  queryRef.current = query;
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  // Callback ref: let the option list scroll by mouse wheel even inside a modal (whose scroll-lock
  // would otherwise eat wheel events over this portalled popover). See useWheelScroll.
  const attachListWheel = useWheelScroll<HTMLDivElement>();
  const multiple = mode === 'multiple' || mode === 'tags';
  const sz = normSize(size);
  const isErr = invalid || status === 'error';

  // Options are kept FLAT (so search, keyboard nav and value lookup index one list), with each grouped
  // option tagged by its group label — the list then draws a sticky header wherever that label changes.
  const opts = useMemo<SelectOption[]>(() => {
    const raw = options ?? fromChildren(children);
    const flat: SelectOption[] = [];
    for (const o of raw as any[]) {
      if (o && Array.isArray(o.options)) flat.push(...o.options.map((c: SelectOption) => ({ ...c, __group: o.label }))); // antd OptGroup { label, options }
      else flat.push(o);
    }
    return flat;
  }, [options, children]);
  const byVal = useMemo(() => new Map(opts.map((o) => [String(o.value), o])), [opts]);
  // Only for a list that is empty BEFORE search — a server-search Select (`onSearch`) is empty by design
  // until the reader types, so it keeps its own wording.
  const emptyList = useEmptyListCause(open && opts.length === 0 && !onSearch);

  /**
   * An unset `Form.Item` hands its control `''` — antd parity, because a controlled `<input>` needs a
   * string and not `undefined`. A Select read that as a real selection: `hasValue` went true, so the
   * trigger dropped its placeholder and then looked up the label of an option that does not exist,
   * leaving an EMPTY unlabelled box. Every Form-bound Select in the app opened that way ("From" and
   * "To" on New Stock Transfer were two blank dropdowns with nothing to say what they wanted).
   *
   * `''` is only a selection when an option actually claims it — the `{ value: '', label: 'All …' }`
   * entries a dozen filter bars use — so that case is preserved exactly and everything else treats
   * the empty string as "nothing chosen".
   */
  const selected: Val[] = useMemo(() => {
    if (value == null) return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr.filter((v) => v !== '' || byVal.has(''));
  }, [value, byVal]);

  const filtered = useMemo(() => {
    if (!showSearch || !query) return opts;
    const q = query.toLowerCase();
    if (typeof filterOption === 'function') return opts.filter((o) => filterOption(query, o));
    if (filterOption === false) return opts;
    return opts.filter((o) => {
      const hay = optionFilterProp === 'value'
        ? String(o.value)
        : optionFilterProp && optionFilterProp !== 'label' && (o as any)[optionFilterProp] != null
          ? String((o as any)[optionFilterProp])
          : `${labelText(o)} ${o.value}`;
      return hay.toLowerCase().includes(q);
    });
  }, [opts, query, showSearch, filterOption, optionFilterProp]);

  // Reset the keyboard-highlight to -1 (nothing pre-highlighted) on EVERY open AND close — not just
  // close — so the first option never looks active before the user hovers/arrows. Resetting on open too
  // makes it robust even when a stale `hi` survives (e.g. React Fast Refresh preserving state on HMR).
  useEffect(() => {
    if (open) { if (showSearch) setTimeout(() => searchRef.current?.focus(), 10); setHi(-1); }
    // Clearing the box must also clear a server search (`onSearch`), or the list reopens still filtered
    // under an empty box. Only when something was typed, so a plain close fires no request.
    else { if (queryRef.current) onSearchRef.current?.(''); setQuery(''); setHi(-1); }
  }, [open, showSearch]);

  const pick = (o: SelectOption) => {
    if (o.disabled) return;
    if (multiple) {
      const has = selected.some((v) => String(v) === String(o.value));
      const next = has ? selected.filter((v) => String(v) !== String(o.value)) : [...selected, o.value];
      onChange?.(next, next.map((v) => byVal.get(String(v)) ?? { value: v }));
      if (query) onSearch?.('');
      setQuery('');
    } else {
      onChange?.(o.value, o);
      setOpen(false);
    }
  };
  const clear = (e: React.MouseEvent) => { e.stopPropagation(); onChange?.(multiple ? [] : undefined, undefined); onClear?.(); };

  const hasValue = selected.length > 0;
  /**
   * A MULTI-SELECT TRIGGER IS ONE LINE HIGH.
   *
   * Chips used to wrap and the control grew a row per line: three statuses on the returns filter bar
   * made the Status box three rows tall, so it burst out of the toolbar and sat over its neighbours.
   * A filter bar lays its controls out on a line — a control that silently becomes 3x its height
   * breaks the row it lives in, and it does it exactly when the filter is in use.
   *
   * So a standalone (toolbar) multi-select collapses to one chip + "+N", which is what two dozen
   * filter bars already ask for by hand with `maxTagCount={1}`. Inside a Form the control owns a
   * full-width row of its own and CAN afford to grow, so a form select still shows every chip — the
   * scheme retailer picker (`maxTagCount={12}`) depends on it.
   */
  const collapse = multiple && (maxTagCount === 'responsive' || (maxTagCount == null && !inForm));
  const shownTags = maxTagCount === 'responsive' ? 2 : typeof maxTagCount === 'number' ? maxTagCount : collapse ? 1 : selected.length;
  // Wrapping is only ever right when more than one chip is meant to be on show; otherwise a single
  // long chip could still push the "+N" counter onto a second line.
  const oneLine = collapse || shownTags <= 1;

  return (
    <Popover.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <Popover.Trigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-label={aria['aria-label']}
          style={style}
          className={cn(
            // Same recess as ui/Input — a select and a text field must read as the same kind of control.
            'group flex items-center gap-2 rounded-control border bg-surface shadow-inset-1 px-3 py-1 text-left text-content transition-colors',
            widthCls,
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-50',
            isErr ? 'border-danger-500' : status === 'warning' ? 'border-warning-500' : 'border-line hover:border-brand-400',
            SIZE[sz], className,
          )}
        >
          <span className={cn('flex min-w-0 flex-1 items-center gap-1 py-0.5', oneLine ? 'flex-nowrap overflow-hidden' : 'flex-wrap')}>
            {!hasValue && <span className="truncate text-subtle">{placeholder}</span>}
            {multiple
              ? selected.slice(0, shownTags).map((v) => {
                  const o = byVal.get(String(v));
                  const hue = o?.chipHue;
                  return (
                    <span
                      key={String(v)}
                      className={cn('inline-flex min-w-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs', !hue && 'bg-surface-3 text-content')}
                      style={hue ? {
                        background: `color-mix(in srgb, ${hue} 16%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 40%, transparent)`,
                        color: hue,
                      } : undefined}
                    >
                      {/* A chip that has given up its slack has to stay readable somehow — same answer
                          as the single-value trigger: the plain-text form the option already carries. */}
                      <span className="min-w-0 truncate" title={o?.title ?? o?.searchLabel ?? undefined}>{o ? chipLabel(o) : String(v)}</span>
                      <span role="button" tabIndex={-1} aria-label="Remove" onClick={(e) => { e.stopPropagation(); pick(o ?? { value: v }); }}
                        className={cn('shrink-0', hue ? 'opacity-60 hover:opacity-100' : 'text-subtle hover:text-content')}>
                        <X className="size-3" />
                      </span>
                    </span>
                  );
                })
              : hasValue && (() => {
                  const o = byVal.get(String(selected[0]));
                  /* A truncated value has to stay READABLE somehow. The trigger is a fixed width and
                     the label inside it is often a node (a code chip, a name, a capsule), so a long
                     one ellipsises with no way back to what it said — "Pune Wa…". Options already
                     carry the plain-text form for search; hovering now shows it. */
                  return (
                    <span className="min-w-0 truncate" title={o?.title ?? o?.searchLabel ?? undefined}>
                      {o ? chipLabel(o) : String(selected[0])}
                    </span>
                  );
                })()}
            {multiple && selected.length > shownTags && <span className="shrink-0 text-xs text-muted">+{selected.length - shownTags}</span>}
          </span>
          {loading && <Spinner className="size-3.5 text-muted" />}
          {/* Clear (✕) and chevron share ONE fixed slot: the ✕ REPLACES the chevron on hover instead of
              appearing beside it, so hovering a clearable Select never adds width or truncates its value
              (e.g. "CHEQUE" → "CHE…"). */}
          <span className="relative flex size-4 shrink-0 items-center justify-center">
            {allowClear && hasValue && !disabled && (
              <span role="button" tabIndex={-1} aria-label="Clear" onClick={clear} className="absolute inset-0 hidden items-center justify-center text-subtle hover:text-content group-hover:flex">
                <X className="size-4" />
              </span>
            )}
            <ChevronDown className={cn('size-4 text-muted', allowClear && hasValue && !disabled && 'group-hover:invisible')} />
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={5}
          /* The list may never grow wider than the room its trigger actually has: a phone-width dialog is
             only `100vw - 2rem` wide, so a viewport-relative cap (92vw) overflowed it on both sides. Radix
             measures the space from the trigger to the collision edge for us; the side padding is what keeps
             the list inside the dialog card instead of flush against the screen. */
          collisionPadding={{ top: 8, bottom: 8, left: 20, right: 20 }}
          className="z-[var(--z-popover)] max-h-[300px] min-w-[var(--radix-popover-trigger-width)] max-w-[min(32rem,var(--radix-popover-content-available-width,92vw))] w-max overflow-hidden rounded-card border border-line bg-surface p-0 shadow-pop"
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, filtered.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
            else if (e.key === 'Enter') { e.preventDefault(); if (filtered[hi]) pick(filtered[hi]); }
          }}
        >
          {showSearch && (
            <div className="flex items-center gap-2 border-b border-line px-3 py-2">
              <SearchIcon className="size-4 shrink-0 text-subtle" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setHi(0); onSearch?.(e.target.value); }}
                placeholder="Search…"
                aria-label="Search options"
                className="w-full bg-transparent text-sm text-content outline-none placeholder:text-subtle"
              />
            </div>
          )}
          <div ref={attachListWheel} className="max-h-[248px] overflow-y-auto overscroll-contain p-1.5" role="listbox">
            {filtered.length === 0 ? (
              <div className="px-2.5 py-6 text-center text-sm text-subtle">
                {opts.length === 0 && (loading || emptyList.cause === 'loading') ? (
                  <span className="inline-flex items-center gap-2"><Spinner className="size-3.5" />Loading…</span>
                ) : opts.length === 0 && emptyList.cause === 'failed' ? (
                  <span className="inline-flex flex-wrap items-center justify-center gap-1.5">
                    This list didn’t load.
                    <button type="button" onClick={emptyList.retry} className="font-medium text-brand-600 hover:underline dark:text-brand-300">Try again</button>
                  </span>
                ) : notFoundContent}
              </div>
            ) : (
              filtered.map((o, i) => {
                const sel = selected.some((v) => String(v) === String(o.value));
                // OptGroup header — drawn only when this option starts a new group in the FILTERED list,
                // so searching never leaves an empty section heading behind.
                const grp = (o as any).__group;
                const header = grp != null && grp !== (filtered[i - 1] as any)?.__group ? (
                  <div className="sticky top-0 z-1 -mx-1.5 bg-surface px-4 pb-1 pt-2 type-overline text-subtle">
                    {grp}
                  </div>
                ) : null;
                return (
                  <Fragment key={String(o.value)}>
                  {header}
                  <div
                    role="option"
                    aria-selected={sel}
                    onMouseEnter={() => setHi(i)}
                    onClick={() => pick(o)}
                    className={cn(
                      'flex cursor-pointer select-none items-center justify-between gap-2 rounded-control px-2.5 py-2 text-sm text-content',
                      o.disabled && 'pointer-events-none opacity-40',
                      // Hover / keyboard-active = NEUTRAL grey (never brand-green) so a pointed-at option
                      // can't be mistaken for the selected one — the SELECTED option is marked by
                      // font-semibold + the brand Check icon instead. Same rule as ui MenuItem.
                      i === hi && 'bg-surface-2 dark:bg-surface-3',
                      sel && 'font-semibold text-brand-700 dark:text-brand-300',
                    )}
                  >
                    {/* No `truncate` — show the FULL option label (the dropdown grows to fit, then wraps
                        past the max-width cap) instead of clipping it to "…". `flex-1` lets an
                        optionRender lay its own row out full-width (e.g. a right-aligned facet count). */}
                    <span className="min-w-0 flex-1">{optionRender ? optionRender(o, { index: i }) : (o.label ?? o.children ?? String(o.value))}</span>
                    {/* The tick's slot is RESERVED on every row, not drawn only on the selected one:
                        a label that right-aligns something (a stock tag, a variant count) would
                        otherwise shift left by the tick's width on exactly one row, leaving the
                        column of tags visibly ragged. */}
                    <span className="flex size-4 shrink-0 items-center justify-center">
                      {sel && <Check className="size-4 text-brand-600" />}
                    </span>
                  </div>
                  </Fragment>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export const Select = Object.assign(SelectBase, { Option: OptionComp, OptGroup });
